import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ReactECharts from "echarts-for-react";
import Datagrid from "../../components/Datagrid/Datagrid";
import { getChipAnalyticsDashboard, getChipKpiDetail } from "../../services/chipAnalyticsService";
import {
	MetricCard,
	SectionTitle,
	USER_ANALYTICS_GRID_SLOT_PROPS,
	USER_ANALYTICS_GRID_SX,
	USER_ANALYTICS_TABLE_GRID_HEIGHT,
} from "./UserAnalytics";

const formatDashDate = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours24 = date.getHours();
	const hours = String(hours24 % 12 || 12).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const seconds = String(date.getSeconds()).padStart(2, "0");
	const meridiem = hours24 >= 12 ? "PM" : "AM";
	return `${month}-${day}-${date.getFullYear()} ${hours}:${minutes}:${seconds} ${meridiem}`;
};

/**
 * ISO string for a date-ish value, or null when unusable — the chip endpoint
 * takes explicit startDate/endDate so the tab follows the page's shared range
 * picker exactly, including custom ranges.
 * @param {Date|string|null} d candidate date
 * @returns {?string} ISO timestamp or null
 */
const toDateParam = (d) => {
	if (!d) return null;
	const dt = d instanceof Date ? d : new Date(d);
	return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
};

/**
 * List card (Top Search Terms, Documents Opened by Category) in the exact same
 * chrome as MetricCard — rounded 10px, soft shadow, hover bottom-border accent —
 * so every card inside a KPI section reads as one family.
 * @param {{title: string, children: React.ReactNode}} props card title + body
 * @returns {JSX.Element} the styled card
 */
const ListCard = ({ title, children }) => (
	<Paper
		elevation={0}
		sx={{
			p: { xs: 2, md: 2.5 },
			height: "100%",
			borderRadius: "10px",
			boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
			borderBottom: "4px solid transparent",
			backgroundColor: "background.paper",
			transition: "all 0.3s ease",
			"&:hover": {
				boxShadow: "0 0 10px 2px rgba(0,0,0,0.16)",
				borderBottomColor: "primary.main",
			},
		}}
	>
		<Typography
			variant="h6"
			color="text.primary"
			sx={{ lineHeight: 1.25, fontWeight: 500, fontSize: { xs: 16, sm: 14, md: 16 }, mb: 1 }}
		>
			{title}
		</Typography>
		{children}
	</Paper>
);

/**
 * "4m 05s"-style display for a duration in seconds ("—" when absent/zero).
 * @param {?number} sec duration in seconds
 * @returns {string} human-readable duration
 */
const formatDuration = (sec) => {
	const s = Math.round(Number(sec) || 0);
	if (!s) return "—";
	const m = Math.floor(s / 60);
	return m ? `${m}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
};

const PAGE_SIZE = 10;

/**
 * Server registry keys for cards whose tooltipKey differs from the drill-down
 * key (both page-view cards share the same underlying navigate events).
 */
const DETAIL_KEY_OVERRIDES = { pagesPerUser: "pagesPerSession" };

/**
 * Timestamp → "YYYY-MM-DD HH:mm:ss" display ("" when absent).
 * @param {?string} v ISO-ish timestamp
 * @returns {string} display value
 */
const fmtTs = (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "");

/**
 * Column presentation for the drill-down grid, keyed by server column field.
 * Display values are pre-formatted on the ROWS (not via valueGetter): MUI
 * DataGrid v7 changed valueGetter's signature to (value, row), which made the
 * old params-style getters silently render blank cells (live-reproduced on the
 * "When" column). renderCell's signature is unchanged, so the chip stays.
 */
const DETAIL_COLUMN_PRESETS = {
	when: { width: 170 },
	user: { flex: 1, minWidth: 160 },
	area: { width: 170 },
	term: { flex: 1, minWidth: 160 },
	no_results: { width: 110, renderCell: (p) => (p?.row?.no_results ? <Chip size="small" label="yes" /> : null) },
	document: { flex: 1.2, minWidth: 180 },
	subject: { flex: 1.2, minWidth: 200 },
	page: { flex: 1.2, minWidth: 160 },
	list: { flex: 1.1, minWidth: 160 },
	part: { flex: 1.2, minWidth: 160 },
	request: { flex: 1.4, minWidth: 200 },
	case_ref: { flex: 1.2, minWidth: 180 },
	wo_ref: { flex: 1.2, minWidth: 170 },
	category: { width: 150 },
	order_number: { width: 150 },
	user_role: { width: 130 },
	conversations: { width: 90 },
	messages: { width: 100 },
	total_seconds: { width: 140, headerName: "Time Spent" },
	last_active: { width: 170 },
};

/**
 * Pre-format a drill-down row's display fields (timestamps, durations) so no
 * column needs a version-sensitive valueGetter.
 * @param {object} r server row
 * @returns {object} row with display-ready values
 */
const formatDetailRow = (r) => ({
	...r,
	when: fmtTs(r.when),
	last_active: fmtTs(r.last_active),
	total_seconds: r.total_seconds != null ? formatDuration(r.total_seconds) : r.total_seconds,
});

/**
 * Drill-down dialog for one KPI card: exact total, KPI-specific breakdowns
 * (top users / terms / documents / categories, counted over ALL matching
 * events), a server-side search box, and the full server-paginated row list.
 * Fetches on open and whenever page/search change; inherits every dashboard
 * filter through `baseQuery`.
 * @param {object} props component props
 * @param {?{key: string, label: string}} props.kpi the clicked card (null = closed)
 * @param {object} props.baseQuery the tab's current filter/date query
 * @param {function} props.onClose close handler
 * @returns {JSX.Element} the dialog
 */
const KpiDetailModal = ({ kpi, baseQuery, onClose }) => {
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState({ page: 0, pageSize: PAGE_SIZE });
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const seqRef = useRef(0);

	// Debounce typing into one server-side filter value.
	useEffect(() => {
		const t = setTimeout(() => setSearch(searchInput.trim()), 400);
		return () => clearTimeout(t);
	}, [searchInput]);

	// New card: reset paging/search/content.
	useEffect(() => {
		setDetail(null);
		setPage({ page: 0, pageSize: PAGE_SIZE });
		setSearchInput("");
		setSearch("");
	}, [kpi?.key]);

	useEffect(() => {
		if (!kpi?.key) return undefined;
		const seq = ++seqRef.current;
		setLoading(true);
		(async () => {
			const resp = await getChipKpiDetail({
				...baseQuery,
				kpi: kpi.key,
				detailPage: page.page + 1,
				detailPageSize: page.pageSize,
				...(search ? { detailSearch: search } : {}),
			});
			if (seq !== seqRef.current) return;
			if (resp?.status && resp?.data) setDetail(resp.data);
			setLoading(false);
		})();
		return undefined;
	}, [kpi?.key, baseQuery, page, search]);

	const columns = (detail?.columns || []).map((c) => ({
		field: c.field,
		headerName: c.headerName,
		...(DETAIL_COLUMN_PRESETS[c.field] || { flex: 1 }),
	}));

	return (
		<Dialog open={Boolean(kpi)} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 6 }}>
				<Typography component="span" variant="h6" sx={{ fontWeight: 700, flex: "1 1 auto" }}>
					{detail?.title || kpi?.label || ""}
				</Typography>
				<Chip size="small" color="primary" label={`Total: ${Number(detail?.total ?? 0).toLocaleString()}`} />
				{detail?.clamped && <Chip size="small" variant="outlined" label="last 90 days" />}
				<IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{(detail?.summaries || []).length > 0 && (
					<Grid container spacing={2} sx={{ mb: 2 }}>
						{(detail?.summaries || []).map((s) => (
							<Grid item xs={12} sm={6} md={4} key={s.key}>
								<Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{s.label}</Typography>
									{(s.rows || []).length === 0 ? (
										<Typography variant="body2" color="text.secondary">No data in this period.</Typography>
									) : (
										<Stack spacing={0.25}>
											{s.rows.map((r, i) => (
												<Stack key={`${s.key}_${i}`} direction="row" justifyContent="space-between" spacing={2}>
													<Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{r.label}</Typography>
													<Typography variant="body2" color="text.secondary">{Number(r.count).toLocaleString()}</Typography>
												</Stack>
											))}
										</Stack>
									)}
								</Paper>
							</Grid>
						))}
					</Grid>
				)}
				{(detail?.columns || []).length > 0 ? (
					<>
						<TextField
							size="small"
							fullWidth
							placeholder="Filter by user, search term, document or order number…"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							sx={{ mb: 1.5 }}
						/>
						<Datagrid
							data={(detail?.rows || []).map(formatDetailRow)}
							columns={columns}
							rowIdField="id"
							pageSize={page.pageSize}
							paginationMode="server"
							rowCount={Number(detail?.total) || 0}
							paginationModel={page}
							onPaginationModelChange={setPage}
							autoHeight={false}
							gridHeight={420}
							loading={loading}
							slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
							gridSx={USER_ANALYTICS_GRID_SX}
						/>
					</>
				) : (
					<Typography variant="body2" color="text.secondary">
						{loading ? "Loading…" : "The chat assistant cannot do this yet. When this feature is added to the chat, the numbers will show here."}
					</Typography>
				)}
			</DialogContent>
		</Dialog>
	);
};

/**
 * The "Ask AI (CHIP)" tab of the User Analytics page: one backend call to
 * /chip-analytics/dashboard (rollup-backed), rendered with the SAME building
 * blocks as the Portal Analytics tab (MetricCard, SectionTitle, square range
 * buttons, outlined table Papers, the shared Datagrid slotProps/sx) so the two
 * tabs read as one dashboard. Lazy by construction: the parent mounts this
 * component only when the tab is first activated.
 *
 * @param {object} props component props
 * @param {object} props.filters the page's shared filter selections
 *   ({ legal_entity, customer_id, site_id, machine_id, user_role[], user_email },
 *   ALL sentinel values) — driven by the shared filter bar above the tabs
 * @param {*} props.allValue the page's ALL_VALUE sentinel
 * @param {object} props.selectedRange the page's selected range preset (label/dates)
 * @param {Array} props.selectedDateRange the page's [startDate, endDate] pair,
 *   including custom picker ranges
 * @returns {JSX.Element} the tab content
 */
const ChipAnalyticsTab = ({ filters = {}, allValue = "All", selectedRange = {}, selectedDateRange = [] }) => {
	const [dashboard, setDashboard] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [sessionsPage, setSessionsPage] = useState({ page: 0, pageSize: PAGE_SIZE });
	const [usersPage, setUsersPage] = useState({ page: 0, pageSize: PAGE_SIZE });
	const [feedbackPage, setFeedbackPage] = useState({ page: 0, pageSize: PAGE_SIZE });
	const [featureUsersPage, setFeatureUsersPage] = useState({ page: 0, pageSize: PAGE_SIZE });
	// The KPI card currently drilled into ({key, label}), null = modal closed.
	const [detailKpi, setDetailKpi] = useState(null);
	// Guards against a slow older response overwriting a newer one — handleRequest
	// carries no AbortController, so staleness is handled by sequence instead.
	const requestSeqRef = useRef(0);

	const query = useMemo(() => {
		// Dates come from the SHARED range picker (presets and custom alike);
		// fall back to the server's 7d default only when the pair is unusable.
		const q = {};
		const startDate = toDateParam(selectedDateRange?.[0] ?? selectedRange?.startDate);
		const endDate = toDateParam(selectedDateRange?.[1] ?? selectedRange?.endDate);
		if (startDate && endDate) {
			q.startDate = startDate;
			q.endDate = endDate;
		} else {
			q.range = "7d";
		}
		if (filters.legal_entity && filters.legal_entity !== allValue) q.legal_entity = filters.legal_entity;
		if (filters.customer_id && filters.customer_id !== allValue) q.customer_id = filters.customer_id;
		if (filters.site_id && filters.site_id !== allValue) q.site_id = filters.site_id;
		if (filters.user_email && filters.user_email !== allValue) q.user_email = filters.user_email;
		// Multi-select roles pass through as an array (the server accepts repeated
		// user_role params); the page's empty array means "all roles".
		if (Array.isArray(filters.user_role) && filters.user_role.length > 0) {
			q.user_role = filters.user_role;
		}
		q.sessionsPage = sessionsPage.page + 1;
		q.sessionsPageSize = sessionsPage.pageSize;
		q.usersPage = usersPage.page + 1;
		q.usersPageSize = usersPage.pageSize;
		q.feedbackPage = feedbackPage.page + 1;
		q.feedbackPageSize = feedbackPage.pageSize;
		q.featureUsersPage = featureUsersPage.page + 1;
		q.featureUsersPageSize = featureUsersPage.pageSize;
		return q;
	}, [selectedDateRange, selectedRange, filters, allValue, sessionsPage, usersPage, feedbackPage, featureUsersPage]);

	useEffect(() => {
		const seq = ++requestSeqRef.current;
		setLoading(true);
		setError("");
		const timer = setTimeout(async () => {
			const resp = await getChipAnalyticsDashboard(query);
			if (seq !== requestSeqRef.current) return; // stale response: a newer request superseded it
			if (resp?.status && resp?.data) {
				setDashboard(resp.data);
			} else {
				setError(resp?.message || "Failed to load CHIP analytics.");
			}
			setLoading(false);
		}, 250);
		return () => clearTimeout(timer);
	}, [query]);

	const k = dashboard?.kpis || {};
	const features = dashboard?.features || [];
	const tilesLoading = loading && !dashboard;

	// Horizontal bars, biggest feature on top — "what do people use CHIP for".
	const featureOption = useMemo(() => {
		const rows = [...features].sort((a, b) => (Number(a.runs) || 0) - (Number(b.runs) || 0));
		return {
			tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
			grid: { left: 12, right: 30, top: 10, bottom: 30, containLabel: true },
			xAxis: { type: "value" },
			yAxis: { type: "category", data: rows.map((f) => f.label) },
			series: [{ name: "Uses", type: "bar", data: rows.map((f) => Number(f.runs) || 0) }],
		};
	}, [features]);

	const sessionColumns = [
		{ field: "chip_session_id", headerName: "Conversation", flex: 1.4, minWidth: 180 },
		{ field: "user_id", headerName: "User", width: 90 },
		{ field: "user_role", headerName: "Role", width: 130 },
		{ field: "started_at", headerName: "Started", width: 205, renderCell: (p) => formatDashDate(p?.row?.started_at) },
		{ field: "messages", headerName: "Msgs", width: 80 },
		{ field: "tool_runs", headerName: "Tools", width: 80 },
		{ field: "write_actions", headerName: "Writes", width: 85 },
		{ field: "orders", headerName: "Orders", width: 85 },
		{ field: "errors", headerName: "Errors", width: 85 },
	];
	const userColumns = [
		{ field: "user_id", headerName: "User ID", width: 90 },
		{ field: "user_name", headerName: "Name", flex: 1, minWidth: 140 },
		{ field: "user_email", headerName: "Email", flex: 1.2, minWidth: 180 },
		{ field: "user_role", headerName: "Role", width: 130 },
		{ field: "conversations", headerName: "Convs", width: 85 },
		{ field: "messages", headerName: "Msgs", width: 80 },
		{ field: "write_actions", headerName: "Writes", width: 85 },
		{ field: "orders", headerName: "Orders", width: 85 },
	];
	const feedbackColumns = [
		{
			field: "user_first_name", headerName: "User", width: 220,
			renderCell: (p) => (
				<Box>
					<Typography variant="body2">{p?.row?.user_first_name || "—"}</Typography>
					{p?.row?.user_email ? <Typography variant="caption" color="text.secondary">{p.row.user_email}</Typography> : null}
				</Box>
			),
		},
		{
			field: "feedback", headerName: "Rating", width: 120,
			renderCell: (p) => p?.row?.feedback === "helpful"
				? "Helpful"
				: p?.row?.feedback === "not_helpful" ? "Not helpful" : (p?.row?.feedback || "—"),
		},
		{
			field: "correction", headerName: "User feedback", flex: 1.5, minWidth: 240,
			renderCell: (p) => p?.row?.correction
				? <Typography variant="body2" noWrap title={p.row.correction}>{p.row.correction}</Typography>
				: <Typography variant="body2" color="text.secondary">No written feedback</Typography>,
		},
		{
			field: "occurred_at", headerName: "Feedback Given date", width: 205,
			renderCell: (p) => formatDashDate(p?.row?.occurred_at),
		},
		{
			field: "suggestion", headerName: "Suggestions", flex: 1.7, minWidth: 260,
			renderCell: (p) => p?.row?.suggestion
				? <Typography variant="body2" noWrap title={p.row.suggestion}>{p.row.suggestion}</Typography>
				: <Typography variant="body2" color="text.secondary">No approved suggestion</Typography>,
		},
		{
			field: "approved_by_first_name", headerName: "Approved by", width: 180,
			renderCell: (p) => (
				<Box>
					<Typography variant="body2">{p?.row?.approved_by_first_name || p?.row?.approved_by_name || "—"}</Typography>
					{p?.row?.approved_by_email ? <Typography variant="caption" color="text.secondary">{p.row.approved_by_email}</Typography> : null}
				</Box>
			),
		},
		{
			field: "approved_at", headerName: "Approval date", width: 205,
			renderCell: (p) => formatDashDate(p?.row?.approved_at),
		},
	];

	/**
	 * One table section, in the Portal Analytics tab's exact dressing: outlined
	 * Paper, bold h6 header row, shared Datagrid slotProps/sx/height.
	 * @param {{title: string, suffix?: string, grid: JSX.Element}} section parts
	 * @returns {JSX.Element} the wrapped section
	 */
	const renderTableSection = ({ title, suffix, grid }) => (
		<Paper variant="outlined" sx={{ p: 2.5 }}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={1}
				sx={{ mb: 1 }}
			>
				<Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
				{suffix ? <Chip size="small" label={suffix} /> : null}
			</Stack>
			{grid}
		</Paper>
	);

	return (
		<Box>
			{/* The date range and all entity filters come from the SHARED filter bar
			    above the tabs — this row only surfaces chip-specific caveats, and
			    renders nothing (zero height) when there is nothing to say. */}
			{((filters.machine_id && filters.machine_id !== allValue)
				|| dashboard?.meta?.userFilterClamped
				|| dashboard?.meta?.roleAccessApplied) && (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					flexWrap="wrap"
					useFlexGap
					spacing={1}
					sx={{ mb: 2 }}
				>
					{(filters.machine_id && filters.machine_id !== allValue) && (
						<Chip
							size="small"
							variant="outlined"
							label="The Machine filter doesn't apply to chat data"
						/>
					)}
					{dashboard?.meta?.userFilterClamped && (
						<Chip size="small" variant="outlined" label="User filter: last 90 days" />
					)}
					{dashboard?.meta?.roleAccessApplied && (
						<Chip size="small" label="Role-restricted view" variant="outlined" />
					)}
				</Stack>
			)}

			{/* Same section card the Portal Analytics tab wraps its KPI sections in
			    (identical padding/border/spacing), so both tabs share one rhythm. */}
			<Paper
				elevation={0}
				sx={{
					p: { xs: 2, md: 2.5 },
					mb: 3,
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					boxShadow: "none",
					backgroundColor: "background.paper",
				}}
			>

			{error && (
				<Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderColor: "error.main" }}>
					<Typography color="error">{error}</Typography>
				</Paper>
			)}

			{/* THE customer question first: which users use CHIP the most, and for
			    which app feature (documents, machines, parts, ordering, services…). */}
			<SectionTitle>What Customers Use Chat For</SectionTitle>
			<Grid container spacing={3} sx={{ mb: 3 }}>
				<Grid item xs={12} lg={5}>
					<Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
						<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>By Area</Typography>
						<ReactECharts option={featureOption} style={{ height: 360 }} notMerge />
					</Paper>
				</Grid>
				<Grid item xs={12} lg={7}>
					{renderTableSection({
						title: "By Feature",
						suffix: dashboard?.featureUsers?.clamped ? "last 90 days" : undefined,
						grid: (
							<Datagrid
								data={dashboard?.featureUsers?.rows || []}
								columns={[
									{ field: "user_name", headerName: "User", flex: 1, minWidth: 140, renderCell: (p) => (
										<Box>
											<Typography variant="body2">{p?.row?.user_name || `User ${p?.row?.user_id}`}</Typography>
											{p?.row?.user_email ? (
												<Typography variant="caption" color="text.secondary">{p.row.user_email}</Typography>
											) : null}
										</Box>
									) },
									{ field: "documents", headerName: "Docs & Manuals", width: 125 },
									{ field: "machines", headerName: "Machines", width: 100 },
									{ field: "parts", headerName: "Parts", width: 90 },
									{ field: "cart", headerName: "Cart", width: 80 },
									{ field: "ordering", headerName: "Ordering", width: 100 },
									{ field: "services", headerName: "Service", width: 95 },
									{ field: "lists", headerName: "Lists", width: 85 },
									{ field: "total", headerName: "Total", width: 90 },
								]}
								rowIdField="user_id"
								pageSize={featureUsersPage.pageSize}
								paginationMode="server"
								rowCount={Number(dashboard?.featureUsers?.total) || 0}
								paginationModel={featureUsersPage}
								onPaginationModelChange={setFeatureUsersPage}
								autoHeight={false}
								gridHeight={420}
								loading={loading}
								slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
								gridSx={USER_ANALYTICS_GRID_SX}
							/>
						),
					})}
				</Grid>
			</Grid>

			<SectionTitle>Chat Engagement</SectionTitle>
			<Grid container spacing={3} sx={{ mb: 3 }}>
				{[
					{ label: "Active Users", value: k.activeUsers, icon: <PeopleAltOutlinedIcon />, tooltipKey: "activeChipUsers" },
					{ label: "Engagement Rate", value: k.engagementRate != null ? `${k.engagementRate}%` : null, icon: <TimelineOutlinedIcon />, tooltipKey: "engagementRate" },
					{ label: "Avg. Session Duration", value: formatDuration(k.avgSessionDurationSec), icon: <AccessTimeOutlinedIcon />, tooltipKey: "avgSessionDuration" },
					{ label: "Sessions / User", value: k.sessionsPerUser, icon: <ForumOutlinedIcon />, tooltipKey: "sessionsPerUser" },
					{ label: "Page Views / Session", value: k.pagesPerSession, helper: "pages CHIP opened", icon: <PageviewOutlinedIcon />, tooltipKey: "pagesPerSession" },
					{ label: "Page Views / User", value: k.pagesPerUser, helper: "pages CHIP opened", icon: <VisibilityOutlinedIcon />, tooltipKey: "pagesPerUser" },
				].map((tile) => (
					// 3 cards per row (2 rows of 3) from md up
					<Grid item xs={12} sm={6} md={4} key={tile.label}>
						<MetricCard
							label={tile.label}
							value={tile.value ?? "—"}
							helper={tile.helper}
							icon={tile.icon}
							tooltipKey={tile.tooltipKey}
							tooltipSource="chip"
							loading={tilesLoading}
							onClick={() => setDetailKpi({
								key: DETAIL_KEY_OVERRIDES[tile.tooltipKey] || tile.tooltipKey,
								label: tile.label,
							})}
						/>
					</Grid>
				))}
			</Grid>

			<SectionTitle>Searching from Chat</SectionTitle>
			{/* 2 cards side by side, half width each */}
			<Grid container spacing={3} sx={{ mb: 3 }}>
				<Grid item xs={12} sm={6}>
					<MetricCard
						label="Searches Performed"
						value={k.searchesPerformed ?? "—"}
						icon={<SearchOutlinedIcon />}
						tooltipKey="searchesPerformed"
						tooltipSource="chip"
						loading={tilesLoading}
						onClick={() => setDetailKpi({ key: "searchesPerformed", label: "Searches Performed" })}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<MetricCard
						label="Searches With No Results"
						value={k.searchesNoResults ?? "—"}
						icon={<SearchOffOutlinedIcon />}
						tooltipKey="searchesNoResults"
						tooltipSource="chip"
						loading={tilesLoading}
						onClick={() => setDetailKpi({ key: "searchesNoResults", label: "Searches With No Results" })}
					/>
				</Grid>
			</Grid>

			<SectionTitle>Models &amp; Documents from Chat</SectionTitle>
			<Grid container spacing={3} sx={{ mb: 3 }}>
				{[
					{ label: "3D Model Opens", value: k.model3dOpens, icon: <ViewInArOutlinedIcon />, tooltipKey: "model3dOpens" },
					{ label: "2D Drawing Opens", value: k.model2dOpens, icon: <ImageOutlinedIcon />, tooltipKey: "model2dOpens" },
					{ label: "Documents Opened", value: k.documentOpens, icon: <DescriptionOutlinedIcon />, tooltipKey: "documentOpens" },
					{ label: "Favorites Saved", value: k.favoritesSaved, icon: <StarBorderOutlinedIcon />, tooltipKey: "favoritesSaved" },
					{ label: "Historical Document Searches", value: k.historicalDocViews, icon: <HistoryOutlinedIcon />, tooltipKey: "historicalDocViews" },
				].map((tile) => (
					// 3 cards per row (2 rows of 3) from md up
					<Grid item xs={12} sm={6} md={4} key={tile.label}>
						<MetricCard
							label={tile.label}
							value={tile.value ?? "—"}
							helper={tile.helper}
							icon={tile.icon}
							tooltipKey={tile.tooltipKey}
							tooltipSource="chip"
							loading={tilesLoading}
							onClick={() => setDetailKpi({
								key: DETAIL_KEY_OVERRIDES[tile.tooltipKey] || tile.tooltipKey,
								label: tile.label,
							})}
						/>
					</Grid>
				))}
			</Grid>

			<SectionTitle>Videos &amp; Orders</SectionTitle>
			<Grid container spacing={3} sx={{ mb: 3 }}>
				{[
					{ label: "Premium Video Views", value: k.premiumVideoViews, icon: <OndemandVideoIcon />, tooltipKey: "premiumVideoViews" },
					{ label: "Premium Interest Clicks", value: k.premiumInterestClicks, icon: <AdsClickIcon />, tooltipKey: "premiumInterestClicks" },
					{ label: "Orders Placed Using Chat", value: k.ordersPlaced, icon: <ShoppingCartCheckoutOutlinedIcon />, tooltipKey: "ordersViaChip" },
				].map((tile) => (
					// 3 cards filling one row, a third each
					<Grid item xs={12} sm={6} md={4} key={tile.label}>
						<MetricCard
							label={tile.label}
							value={tile.value ?? "—"}
							helper={tile.helper}
							icon={tile.icon}
							tooltipKey={tile.tooltipKey}
							tooltipSource="chip"
							loading={tilesLoading}
							onClick={() => setDetailKpi({
								key: DETAIL_KEY_OVERRIDES[tile.tooltipKey] || tile.tooltipKey,
								label: tile.label,
							})}
						/>
					</Grid>
				))}
			</Grid>

			<SectionTitle>App Features Used from Chat</SectionTitle>
			<Grid container spacing={3} sx={{ mb: 3 }}>
				{(dashboard?.actionKpis || [])
					// Per review 2026-10-14: cart/order actions are already covered by the
					// Videos & Orders section and the funnel — hide the overlapping cards.
					.filter((action) => !["add_to_cart", "buy_again", "top_orders", "request_list", "rspl", "recommendations"].includes(action.key))
					.map((action) => (
					// 3 cards per row from md up (5 cards -> 3+2)
					<Grid item xs={12} sm={6} md={4} key={action.key}>
						<MetricCard
							label={action.label}
							value={action.runs ?? "—"}
							icon={<BoltOutlinedIcon />}
							tooltipKey={action.key}
							tooltipSource="chip"
							loading={tilesLoading}
							onClick={() => setDetailKpi({ key: action.key, label: action.label })}
						/>
					</Grid>
				))}
			</Grid>

			</Paper>

			<Grid container spacing={3}>
					{/* full-width table */}
					<Grid item xs={12}>
					{renderTableSection({
						title: "Feedback Log",
						suffix: dashboard?.feedback?.clamped ? "last 90 days" : undefined,
						grid: (
							<Datagrid
								data={(dashboard?.feedback?.rows || []).map((f, i) => ({ id: `${f.audit_id || i}_${i}`, ...f }))}
								columns={feedbackColumns}
								rowIdField="id"
								pageSize={feedbackPage.pageSize}
								paginationMode="server"
								rowCount={Number(dashboard?.feedback?.total) || 0}
								paginationModel={feedbackPage}
								onPaginationModelChange={setFeedbackPage}
								autoHeight={false}
								gridHeight={USER_ANALYTICS_TABLE_GRID_HEIGHT}
								loading={loading}
								slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
								gridSx={USER_ANALYTICS_GRID_SX}
							/>
						),
					})}
				</Grid>
			</Grid>

			<KpiDetailModal kpi={detailKpi} baseQuery={query} onClose={() => setDetailKpi(null)} />
		</Box>
	);
};

export default ChipAnalyticsTab;
